import DateFnsUtils from '@date-io/date-fns';
import {Button, Divider} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import {Roles} from 'app/Constants';
import {Title} from 'components/forms';
import useRole from 'components/GenericsHooks/useRoles';
import {Loading} from 'components/grid-dx/loading/loading';
import {AddingOptions, EditingOptions} from 'components/grid-dx/types';
import useCollaborator from 'components/hooks/useCollaborator';
import AddPDF from 'features/edit-course/components/DropZone';
import {Form, Formik} from 'formik';
import React from 'react';
import {ObjectSchema} from 'yup';
import {i18n2} from '../../features/i18n/i18n';
import {defaultObject, fields} from '../../helpers/defaults';
import {
  AutocompleteMultiname,
  AutocompleteSelectMultiple,
  AutocompleteSelectMultipleFiltered,
  DateByCourse,
  Enum,
  FilteredAutocomplete,
  FilteredAutocompleteByCourse,
  Input,
  InputDate,
  SelectAutocomplete,
  Switch,
} from './GenericForm/index';
import Signature from './Signature';
import CustomAlert from 'components/grid-dx/alert';
import PasswordRulesHint from 'features/collaborator/component/PasswordRulesHint';
import {useLoadParameter} from 'features/parameter/hook/useLoadParameter';

interface FormProps {
  //Mejorar esto!
  schema: ObjectSchema;
  promise: any;
  row?: any;
  setOpen: any;
  open: boolean;
  type: string;
  lookup: any[];
  columnOptions: any[];
  initialValues?: any;
  refresh: any;
}

type FormMessage = {
  text: string | React.ReactNode;
  open: boolean;
  successful: boolean;
};

const MIN_SUCCESS_CLOSE_DELAY_MS = 1200;
const MAX_SUCCESS_CLOSE_DELAY_MS = 4500;
const BASE_SUCCESS_CLOSE_DELAY_MS = 900;
const MS_PER_SUCCESS_CHAR = 22;

const getSuccessCloseDelayMs = (title: string, warnings?: string[]) => {
  const fullMessage = [title, ...(warnings ?? [])].join(' ');
  const computedDelay =
    BASE_SUCCESS_CLOSE_DELAY_MS + fullMessage.length * MS_PER_SUCCESS_CHAR;

  return Math.min(
    MAX_SUCCESS_CLOSE_DELAY_MS,
    Math.max(MIN_SUCCESS_CLOSE_DELAY_MS, computedDelay),
  );
};

const FormEditor = ({
  initialValues,
  columnOptions,
  schema,
  promise,
  row = undefined,
  setOpen,
  open,
  type,
  lookup,
  refresh,
}: FormProps) => {
  const role = useRole();
  const collaborator = useCollaborator();
  const [valuesToFormik, setvaluesToFormik] = React.useState<any>({});
  const [message, setMessage] = React.useState<FormMessage>({
    text: '',
    open: false,
    successful: false,
  });
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [loading, setLoading] = React.useState(false);
  const {
    data: settings,
    loading: settingsLoading,
    error: settingsError,
  } = useLoadParameter();

  const resetForm = () => {
    setvaluesToFormik({...defaultObject(schema), ...initialValues});
    setOpen(false);
  };

  React.useEffect(() => {
    if (typeof row !== 'undefined') {
      row.reason = '';
    }
    setvaluesToFormik({...defaultObject(schema), ...initialValues});
  }, [initialValues, row, schema]);

  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const submit = (values: any, formikBag: any) => {
    setvaluesToFormik({...values});
    // If editing and password hasn't changed (still equals passwordOriginal),
    // remove the password field so backend doesn't receive encrypted value.
    if (
      values.passwordOriginal &&
      values.password === values.passwordOriginal
    ) {
      delete values.password; // don't send encrypted password back
    }
    // remove helper field before sending
    if (values.passwordOriginal) delete values.passwordOriginal;

    values = {
      ...values,
      user: initialValues.updatedBy,
    };
    setLoading(true);

    const buildSuccessMessage = (title: string, warnings?: string[]) => (
      <div>
        <div
          style={{
            fontWeight: 600,
            marginBottom: warnings && warnings.length > 0 ? 8 : 0,
          }}>
          {title}
        </div>

        {warnings && warnings.length > 0 && (
          <ul style={{paddingLeft: 20, margin: 0}}>
            {warnings.map((w: string, idx: number) => (
              <li key={idx}>{w}</li>
            ))}
          </ul>
        )}
      </div>
    );

    const scheduleCloseAfterSuccess = (delayMs: number) => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      closeTimeoutRef.current = setTimeout(() => {
        handleClose();
        closeTimeoutRef.current = null;
      }, delayMs);
    };

    if (type === 'edit') {
      //CUANDO EDITAS
      const editingOptions: EditingOptions = {
        row: row,
        changed: values,
      };
      promise(editingOptions).then((response: any) => {
        if (response.status === 200) {
          response.text().then((text: string) => {
            const result = text ? JSON.parse(text) : {};
            const successTitle = i18n2._('Se ha editado con éxito');
            const closeDelayMs = getSuccessCloseDelayMs(
              successTitle,
              result?.warnings,
            );
            formikBag.setSubmitting(false);
            setMessage({
              open: true,
              successful: true,
              text: buildSuccessMessage(successTitle, result?.warnings),
            });
            scheduleCloseAfterSuccess(closeDelayMs);
          });
        } else {
          response.text().then((message: string) =>
            setMessage({
              text: response.statusText + ': ' + message,
              open: true,
              successful: false,
            }),
          );
          formikBag.setFieldValue('pass', '');
          setLoading(false);
        }
      });
    } else {
      //CUANDO AGREGAS
      const addingOptions: AddingOptions = {
        added: values,
      };
      promise(addingOptions).then((response: any) => {
        if (response.status === 200) {
          response.text().then((text: string) => {
            const result = text ? JSON.parse(text) : {};
            const successTitle = i18n2._('Se ha creado con éxito');
            const closeDelayMs = getSuccessCloseDelayMs(
              successTitle,
              result?.warnings,
            );
            formikBag.setSubmitting(false);
            setMessage({
              open: true,
              successful: true,
              text: buildSuccessMessage(successTitle, result?.warnings),
            });
            scheduleCloseAfterSuccess(closeDelayMs);
          });
        } else {
          response.text().then((message: string) =>
            setMessage({
              text: response.statusText + ': ' + message,
              open: true,
              successful: false,
            }),
          );
          formikBag.setFieldValue('pass', '');
          setLoading(false);
        }
      });
    }
    formikBag.setSubmitting(false);
  };

  const handleClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setvaluesToFormik({...defaultObject(schema), ...initialValues});
    setMessage({
      text: '',
      open: false,
      successful: false,
    });
    setOpen(false);
    setLoading(false);
    refresh.current();
  };

  const handleCloseSnackbar = () => {
    setMessage({
      text: '',
      open: false,
      successful: false,
    });
    setLoading(false);
  };

  if (settingsLoading) return null;
  if (settingsError) return null;
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
        fullWidth={true}>
        {type === 'add' ? (
          <Title onClose={handleClose} title={i18n2._('Add')} />
        ) : (
          <Title onClose={handleClose} title={i18n2._('Edit')} />
        )}
        <Divider />
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Formik
            initialValues={
              type === 'edit'
                ? (() => {
                    const passwordPlaceholder =
                      (row && row.password) || 'PASSWORD_UNCHANGED';
                    return {
                      ...row,
                      password: passwordPlaceholder,
                      passwordOriginal: passwordPlaceholder,
                    };
                  })()
                : {
                    ...valuesToFormik,
                  }
            }
            validationSchema={schema}
            onSubmit={submit}
            validateOnChange
            enableReinitialize>
            {(formikProps) => (
              <Form style={{padding: 15}} noValidate autoComplete="off">
                <Grid style={{padding: 20}} container spacing={3}>
                  {fields(schema).map((props) => {
                    // Mapeamos el schema.
                    const options = columnOptions.find(
                      (opt: any) => opt.column === props.field,
                    ); //Mapeamos las column Options
                    let {
                      allowEdit = true,
                      allowAdd = true,
                      hiddenAdd = false,
                      hiddenEdit = false,
                      fullWidth = true,
                      multiline = false,
                      sizeDesktop = 6,
                      sizeMobile = 12,
                      selectType = null,
                      data = [],
                      required = false,
                      keyForFilter = null,
                      keyForFilterSecond = null,
                      column = '',
                      myLabel = '#',
                      lookupColumn = '',
                      disabledPast = false,
                      showAll = false,
                      allowDisabled = null,
                    } = options || {}; //sacamos las options
                    const disabled =
                      (type === 'add' && !allowAdd) ||
                      (type === 'edit' && !allowEdit);
                    const hidden =
                      (type === 'add' && hiddenAdd) ||
                      (type === 'edit' && hiddenEdit);
                    if (hidden) return null;
                    if (props.type === 'boolean') {
                      sizeDesktop = 3;
                    }

                    const passwordHelper =
                      props.field === 'password' ? (
                        <PasswordRulesHint
                          password={formikProps.values.password}
                          context={formikProps.values}
                          settings={settings}
                        />
                      ) : undefined;

                    return (
                      <Grid item md={sizeDesktop} xs={sizeMobile}>
                        {selectType === 'multipleName' ? (
                          <AutocompleteMultiname
                            {...props}
                            myLabel={myLabel}
                            disabled={disabled}
                            lookup={lookup}
                            fullWidth={fullWidth}
                            lookupColumn={lookupColumn}
                            formikProps={formikProps}
                          />
                        ) : null}
                        {selectType === 'dateByCourse' ? (
                          <DateByCourse
                            {...props}
                            disabledPast={disabledPast}
                            fullWidth={fullWidth}
                            required={required}
                            disabled={disabled}
                            lookup={lookup}
                            formikProps={formikProps}
                            typeAddOrEdit={type}
                          />
                        ) : null}
                        {selectType === 'filterByFetch' ? (
                          <FilteredAutocompleteByCourse
                            {...props}
                            myLabel={myLabel}
                            column={column}
                            lookup={lookup}
                            keyForFilter={keyForFilter}
                            formikProps={formikProps}
                            fullWidth={fullWidth}
                            disabled={disabled}
                          />
                        ) : null}
                        {selectType === 'limitedByColumn' ? (
                          <FilteredAutocomplete
                            {...props}
                            myLabel={myLabel}
                            column={column}
                            lookup={lookup}
                            keyForFilter={keyForFilter}
                            formikProps={formikProps}
                            fullWidth={fullWidth}
                            disabled={disabled}
                            showAll={showAll}
                            allowDisabled={allowDisabled}
                          />
                        ) : null}
                        {selectType === 'enum' ? (
                          <Enum
                            {...props}
                            data={data}
                            disabled={disabled}
                            fullWidth={fullWidth}
                            customValidate={undefined}
                            variant="outlined"
                          />
                        ) : null}
                        <SelectAutocomplete
                          {...props}
                          myLabel={myLabel}
                          disabled={disabled}
                          formikProps={formikProps}
                          lookup={lookup}
                          fullWidth={fullWidth}
                          user={collaborator}
                          id={type}
                        />
                        {selectType !== 'enum' ? (
                          <Input
                            {...props}
                            myLabel={myLabel}
                            disabled={disabled}
                            lookup={lookup}
                            multiline={multiline}
                            fullWidth={fullWidth}
                            required={required}
                            typeAddOrEdit={type}
                            formikProps={{
                              ...formikProps,
                              helperTextOverride: passwordHelper,
                            }}
                          />
                        ) : null}

                        {props.type === 'boolean' ? (
                          <Switch
                            {...props}
                            disabled={disabled}
                            formikProps={formikProps}
                            myLabel={myLabel}
                            fullWidth={fullWidth}
                          />
                        ) : null}
                        {props.type === 'date' &&
                        selectType !== 'dateByCourse' ? (
                          <InputDate
                            {...props}
                            disabledPast={disabledPast}
                            fullWidth={fullWidth}
                            required={required}
                            disabled={disabled}
                          />
                        ) : null}
                        {selectType === 'multiple' ? (
                          <AutocompleteSelectMultiple
                            {...props}
                            myLabel={myLabel}
                            disabled={disabled}
                            formikProps={formikProps}
                            lookup={lookup}
                            fullWidth={fullWidth}
                            user={collaborator}
                            id={type}
                          />
                        ) : null}
                        {selectType === 'multipleLimitedByColumn' ? (
                          <AutocompleteSelectMultipleFiltered
                            {...props}
                            myLabel={myLabel}
                            disabled={disabled}
                            formikProps={formikProps}
                            lookup={lookup}
                            fullWidth={fullWidth}
                            user={collaborator}
                            id={type}
                            keyForFilter={keyForFilter}
                            keyForFilterSecond={keyForFilterSecond}
                            showAll={showAll}
                          />
                        ) : null}
                        {selectType === 'PDF' && role === Roles.AdminQA ? (
                          <AddPDF
                            setFieldValue={formikProps.setFieldValue}
                            isEditing={false}
                            pdfName={formikProps.values.pdfName}
                            fieldName={'pdfName'}
                            extension={'PDF'}
                          />
                        ) : null}
                      </Grid>
                    );
                  })}
                </Grid>
                <Signature
                  user={initialValues.updatedBy}
                  customPadding={null}
                  errors={formikProps.errors}
                  touched={formikProps.touched}
                />
                <div style={{float: 'right', padding: 10}}>
                  <Button onClick={resetForm} className="red-text">
                    {i18n2._('Cancelar')}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={formikProps.isSubmitting}>
                    {i18n2._('Confirmar')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </MuiPickersUtilsProvider>
        {loading && <Loading />}
        <CustomAlert
          handleClose={handleCloseSnackbar}
          message={message.text}
          ms={6000}
          open={message.open}
          successful={message.successful}
        />
      </Dialog>
    </>
  );
};

export default FormEditor;
